import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import Button from '../../../../../components/Form/Button'
import Input from '../../../../../components/Form/Input'

export interface CancellationSummaryRow {
    label: string
    value: React.ReactNode
    valueClassName?: string
}

interface CancellationPanelProps {
    title: string
    subtitle?: string
    warningMessage: React.ReactNode
    extraContent?: React.ReactNode
    reasonLabel?: string
    reasonPlaceholder?: string
    confirmActionLabel?: string
    dismissActionLabel?: string
    confirmModalTitle?: string
    confirmModalDescription?: string
    summaryRows?: CancellationSummaryRow[]
    isSubmitting: boolean
    onConfirm: (reason: string) => void
    onDismiss: () => void
}

const CancellationPanel: React.FC<CancellationPanelProps> = ({
    title,
    subtitle,
    warningMessage,
    extraContent,
    reasonLabel = 'Reason',
    reasonPlaceholder = 'Describe the reason...',
    confirmActionLabel = 'Confirm',
    dismissActionLabel = 'Cancel',
    confirmModalTitle,
    confirmModalDescription = 'This action is permanent and cannot be undone.',
    summaryRows = [],
    isSubmitting,
    onConfirm,
    onDismiss,
}) => {
    const [reason, setReason] = useState('')
    const [showConfirm, setShowConfirm] = useState(false)
    const confirmLockedRef = useRef(false)

    useEffect(() => {
        if (!isSubmitting) {
            confirmLockedRef.current = false
        }
    }, [isSubmitting])

    const handleConfirmSubmit = async () => {
        if (confirmLockedRef.current || isSubmitting) return
        confirmLockedRef.current = true
        await Promise.resolve(onConfirm(reason))
    }

    return (
        <>
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="py-2 px-4 font-semibold rounded-t-lg bg-indigo-100 dark:bg-slate-700">
                    {title}
                    {subtitle && (
                        <p className="text-sm font-normal text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
                    )}
                </div>
                <div className="p-4 space-y-4 bg-white dark:bg-slate-800">
                    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                        <ExclamationCircleIcon className="w-4 h-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-red-700 dark:text-red-300">{warningMessage}</div>
                    </div>
                    {extraContent}
                    <Input
                        label={reasonLabel}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={reasonPlaceholder}
                        required
                    />
                    <div className="flex gap-2">
                        <Button
                            color="red"
                            disabled={!reason.trim() || isSubmitting}
                            onClick={() => setShowConfirm(true)}
                        >
                            {confirmActionLabel}
                        </Button>
                        <Button
                            color="slate"
                            disabled={isSubmitting}
                            onClick={onDismiss}
                        >
                            {dismissActionLabel}
                        </Button>
                    </div>
                </div>
            </div>

            {showConfirm && createPortal(
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 space-y-5">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 shrink-0">
                                <ExclamationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                    {confirmModalTitle ?? confirmActionLabel + '?'}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {confirmModalDescription}
                                </p>
                            </div>
                        </div>
                        <div className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
                            {summaryRows.map((row, i) => (
                                <div key={i} className="flex justify-between px-4 py-2">
                                    <span className="text-gray-500 dark:text-gray-400">{row.label}</span>
                                    <span className={row.valueClassName ?? 'font-medium dark:text-gray-200'}>
                                        {row.value}
                                    </span>
                                </div>
                            ))}
                            <div className="flex justify-between items-start px-4 py-2 gap-4">
                                <span className="text-gray-500 dark:text-gray-400 shrink-0">Reason</span>
                                <span className="font-medium dark:text-gray-200 text-right">{reason}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button
                                color="slate"
                                onClick={() => setShowConfirm(false)}
                                disabled={isSubmitting}
                            >
                                Go back
                            </Button>
                            <Button
                                color="red"
                                isSaving={isSubmitting}
                                disabled={isSubmitting}
                                onClick={handleConfirmSubmit}
                            >
                                {isSubmitting ? 'Processing...' : confirmActionLabel}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}

export default CancellationPanel
